import { useQuery } from '@tanstack/react-query'
import { BenchmarkSnapshot } from 'enrollment-platform/pages/CustomerManagement/pages/ViewCustomer/tabs/Contribution/types/types'
import { DEFAULT_BENCHMARK_LABEL } from 'enrollment-platform/pages/CustomerManagement/pages/ViewCustomer/tabs/Contribution/utils'
import { useMemo } from 'react'
import { getBenchmarkSnapshots } from '../api/demo-customer.api'

/**
 * Same key the contributions tab uses, so both screens share one cached
 * snapshot list.
 */
export const BENCHMARK_SNAPSHOTS_QUERY_KEY = ['getBenchmarkPlans']

/**
 * Snapshot pre-selected for demo populate when it is available. It only
 * exists in some environments; everywhere else the default is live pricing.
 */
export const PREFERRED_DEMO_BENCHMARK_SNAPSHOT_ID = 110

export interface BenchmarkSnapshotOption {
  /** Snapshot id, or null for live pricing ("Default - Live Table"). */
  value: number | null
  label: string
}

interface BenchmarkSnapshotOptions {
  options: BenchmarkSnapshotOption[]
  /** PREFERRED_DEMO_BENCHMARK_SNAPSHOT_ID when selectable, otherwise null. */
  defaultSnapshotId: number | null
  isLoading: boolean
}

const isSelectable = (snapshot: BenchmarkSnapshot): boolean =>
  snapshot.isEnabled && !snapshot.deletedAt

/**
 * Benchmark snapshot choices for the demo populate step, in the same shape as
 * the contributions tab dropdown: live pricing first, then every enabled,
 * non-deleted snapshot.
 */
export function useBenchmarkSnapshotOptions(): BenchmarkSnapshotOptions {
  const { data: snapshots, isPending } = useQuery({
    queryKey: BENCHMARK_SNAPSHOTS_QUERY_KEY,
    queryFn: getBenchmarkSnapshots,
    gcTime: 0,
  })

  return useMemo(() => {
    const selectable = (snapshots ?? []).filter(isSelectable)
    const hasPreferred = selectable.some(
      (snapshot) => snapshot.id === PREFERRED_DEMO_BENCHMARK_SNAPSHOT_ID
    )
    return {
      options: [
        { value: null, label: DEFAULT_BENCHMARK_LABEL },
        ...selectable.map((snapshot) => ({
          value: snapshot.id,
          label: snapshot.title,
        })),
      ],
      defaultSnapshotId: hasPreferred
        ? PREFERRED_DEMO_BENCHMARK_SNAPSHOT_ID
        : null,
      isLoading: isPending,
    }
  }, [snapshots, isPending])
}
